import { useCallback, useEffect, useState } from "react";

// Daily concierge-message budget status for the ambient "messages left
// today" meter. Reads GET /api/usage (read-only; the count is incremented
// server-side per message in /api/concierge). Refetch after each message
// so the meter stays current.

export interface UsageStatus {
  limit: number;
  used: number;
  remaining: number;
  resetAt: string | null;
}

// Local calendar day + UTC offset so the server resets the budget at the
// user's local midnight. Mirrors what the concierge request sends.
export function localDayParams(): { localDate: string; tzOffsetMinutes: number } {
  const now = new Date();
  const tzOffsetMinutes = now.getTimezoneOffset();
  // Local YYYY-MM-DD (not UTC): build from local getters.
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return { localDate: `${y}-${m}-${d}`, tzOffsetMinutes };
}

// Pass the Supabase access token (session.access_token) when signed in so
// the meter reads the per-USER budget, matching what the concierge enforces.
// Omit it when anonymous (per-IP). Refetches when the token changes so the
// meter re-scopes on sign in/out.
export function useUsage(accessToken?: string): UsageStatus & {
  loading: boolean;
  refetch: () => void;
  // Apply a known status (e.g. from a daily_limit error) without a fetch.
  setFromLimit: (resetAt: string | null, limit?: number) => void;
} {
  const [status, setStatus] = useState<UsageStatus>({
    limit: 0,
    used: 0,
    remaining: Infinity, // unknown-yet: don't show "0 left" before first load
    resetAt: null,
  });
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(() => {
    const { localDate, tzOffsetMinutes } = localDayParams();
    const qs = new URLSearchParams({
      localDate,
      tzOffsetMinutes: String(tzOffsetMinutes),
    });
    const headers: Record<string, string> = {};
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    fetch(`/api/usage?${qs}`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data.remaining === "number") {
          setStatus({
            limit: data.limit ?? 0,
            used: data.used ?? 0,
            remaining: data.remaining,
            resetAt: data.resetAt ?? null,
          });
        }
      })
      .catch(() => {
        /* non-fatal: meter just stays hidden/unknown */
      })
      .finally(() => setLoading(false));
    // Re-scope the meter when the auth token changes (sign in/out).
  }, [accessToken]);

  const setFromLimit = useCallback((resetAt: string | null, limit?: number) => {
    setStatus((prev) => ({
      limit: limit ?? prev.limit,
      used: limit ?? prev.limit,
      remaining: 0,
      resetAt: resetAt ?? prev.resetAt,
    }));
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...status, loading, refetch, setFromLimit };
}
